import {Component, EventEmitter, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, Output, ChangeDetectionStrategy} from '@angular/core';
import {EntityTailConfig} from '../../../core/models/EntityTailConfig';
import {QueryConfig} from '../../../core/models/QueryConfig';
import {Store} from '../../../store';
import {ActivatedRoute, Router, RoutesRecognized} from '@angular/router';
import {TradeSimulation} from '../../../core/models/TradeSimulation';
import {NewDerivativeCollateralModalComponent} from '../new-derivative-collateral-modal/new-derivative-collateral-modal.component';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ConstantsService} from '../../../core/services/constants.service';
import {NotificationPopupComponent} from '../notification-popup/notification-popup.component';
import {DataService} from '../../../core/services/data.service';
import {UtilityService} from '../../../core/services/utility.service';
import {ApplicationEventsService} from '../../../core/services/application-events.service';
import {User} from '../../../core/models/User';
import {Subject, combineLatest} from 'rxjs';
import {tap, takeUntil, filter, map, distinctUntilChanged} from 'rxjs/operators';
import * as _ from 'underscore';
import  {isNil, isUndefined, isEqual, find} from 'lodash-es';
import moment from 'moment';
import { InstrumentItem } from '../../../core/models/InstrumentItem';
import { NotifPollingService } from '../../../core/services/notif-polling.service';
import { SimulationNotification } from '../../../core/models/SimulationNotification';

@Component({
    selector: 'app-modelling-panel',
    templateUrl: './modelling-panel.component.html',
    styleUrls: ['./modelling-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ModellingPanelComponent implements OnInit, OnDestroy, OnChanges {
    @Input()
    showModelOverlay: boolean;

    @Input()
    trades: any[];

    @Input()
    tradeDataLoading: boolean;

    @Input()
    enableModels: boolean = true;

    @Output()
    runModelConfig = new EventEmitter<boolean>();

    @Output()
    refreshModelList = new EventEmitter<boolean>();

    modellingConfig: QueryConfig;
    groupTailConfig: EntityTailConfig;
    pageUrl: string;
    legalEntityName: string;
    legalEntityId: string;
    tradeSimulations: TradeSimulation[];
    selectedModel: TradeSimulation;
    modelStatus: any;
    user: User = null;
    private unsubscribe: Subject<void> = new Subject();
    instr: string;

    constructor(private modalService: NgbModal, private store: Store, public router: Router, private route: ActivatedRoute,
                private constantsService: ConstantsService, private dataService: DataService, private utility: UtilityService,
                private  appEvents: ApplicationEventsService, private notifPollingService: NotifPollingService) {
    }

    ngOnInit() {
        this.modelStatus = this.constantsService.modelStatus;
        const user$ = this.store.select<User>('user').pipe(
            filter(user => !isNil(user)),
            tap(user => this.user = user)
        );
        const routes$ = this.router.events.pipe(
            filter(val => val instanceof RoutesRecognized)
        );
        combineLatest(user$, routes$).pipe(takeUntil(this.unsubscribe)).subscribe(([user, route]) => {
            const savedEntityId = this.legalEntityId;
            const val = route as RoutesRecognized;
            this.pageUrl = val.url;
            this.legalEntityId = val.state.root.firstChild.params.entityId;
            this.legalEntityName = this.computeLegalEntityName(this.legalEntityId);
            this.modellingConfig = this.store.value.modellingConfig;
            this.groupTailConfig = {
                legalEntity: undefined,
                yearRangeTails: this.modellingConfig.groupTailConfig
            };
            if (savedEntityId != this.legalEntityId) {
                this.refreshModelList.emit(true);
            }
        });
        this.notifPollingService.publishedNotifs$
            .pipe(
                distinctUntilChanged((prev: SimulationNotification[],curr: SimulationNotification[]) => isEqual(prev, curr))
            )
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((notifs: SimulationNotification[]) => {
                let needUpdate = false;
                for (const notif of notifs) {
                    // console.log(`search ${notif.modelID} (${notif.status}) in trades`);
                    const found = find(this.trades, ['modelId', notif.modelID]);
                    // if (found) { console.log(`found trade with status ${found.status}`)}
                    if (found && found.status !== notif.status) {
                        needUpdate = true;
                        break;
                    }
                }
                if (needUpdate) {
                    // console.log(`needUpdate => reloadPageData`);
                    this.refreshModelList.emit(true);
                }
            });
        this.instr = this.dumpInstruments();
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.selectedModel) {
            if (!isUndefined(changes.trades)) {
                for (const trade of changes.trades.currentValue) {
                    if (this.selectedModel.modelId === trade.modelId) {
                        this.selectedModel = trade;
                        break;
                    }
                }
            }
        }
    }

    onModelRun() {
        if (this.selectedModel && this.selectedModel.status && this.selectedModel.status == this.constantsService.modelStatus.expired) {
            const modalRef = this.modalService.open(NotificationPopupComponent,
                {centered: true, windowClass: 'notification-modal', backdrop: 'static'});
            modalRef.result.then((result) => {
                if (result === 'ok') {
                    this.runModel();    //TODO: this should be a call to put the simulation request and not to show the data
                }
            }).catch(() => {
            });
            modalRef.componentInstance.notificationPopupMode = this.constantsService.notificationPopupMode.modelSimulationRequest;
        }
        else {
            this.runModel();
        }
    }

    runModel() {
        if (!this.store.value.urlModelToggle[this.router.url]) {
            this.store.value.urlModelToggle[this.router.url] = true;
            this.store.set('urlModelToggle', this.store.value.urlModelToggle);
            this.store.set('modellingConfig', this.modellingConfig);
        }
        this.reloadPageData();
        this.showModelOverlay = false;
        this.runModelConfig.emit(true);
    }

    onEditTrade() {
        const modalRef = this.modalService.open(NewDerivativeCollateralModalComponent,
            {centered: true, windowClass: 'model-instruments-modal'});
        modalRef.result.then((result) => {
            result.lastUpdated = new Date();
            result.userName = this.getUserName();
            this.createTradeSimulation(result);
        }, (reason) => {
        }).catch(() => {
        });
        modalRef.componentInstance.mode = this.constantsService.modelPopupMode.edit;
        modalRef.componentInstance.tradeSimulation = this.selectedModel;
        modalRef.componentInstance.legalEntityName = this.legalEntityName;
    }

    onDeleteTrade() {
        const modalRef = this.modalService.open(NotificationPopupComponent,
            {centered: true, windowClass: 'notification-modal', backdrop: 'static'});
        modalRef.result.then((result) => {
            if (result === 'ok') {
                this.deleteTradeSimulation(this.selectedModel);
            }
        }).catch((err) => {
            console.log('onDeleteTradeError:', err);
        });
        modalRef.componentInstance.notificationPopupMode = this.constantsService.notificationPopupMode.deleteModel;
        modalRef.componentInstance.legalEntityName = this.legalEntityName;
    }

    onTradeExecuted() {
        const modalRef = this.modalService.open(NewDerivativeCollateralModalComponent,
            {centered: true, windowClass: 'model-instruments-modal'});
        modalRef.result.then((result: TradeSimulation) => {
            this.createTradeSimulation(result);
        }, (reason) => {
        }).catch(() => {
        });
        modalRef.componentInstance.mode = this.constantsService.modelPopupMode.tradeExecuted;
        modalRef.componentInstance.tradeSimulation = this.selectedModel;
        modalRef.componentInstance.legalEntityName = this.legalEntityName;
    }

    onCreateNew() {
        const modalRef = this.modalService.open(NewDerivativeCollateralModalComponent,
            {centered: true, windowClass: 'model-instruments-modal', backdrop: 'static'});
        modalRef.result.then((result: TradeSimulation) => {
            result.createdDate = new Date();
            result.userName = this.getUserName();
            this.createTradeSimulation(result);
        }, (reason) => {
        }).catch(() => {
        });
        modalRef.componentInstance.mode = this.constantsService.modelPopupMode.create;
        modalRef.componentInstance.legalEntityName = this.legalEntityName;
    }

    public createTradeSimulation(tradeSimulation: TradeSimulation) {
        const simu = this.utility.simuTxCallback(tradeSimulation);
        this.dataService.createTradeSimulation(simu).subscribe(success => {
            if (!success) {
                alert('Error happened while creating new simulation model.');
            } else {
                this.refreshModelList.emit(true);
            }
        }, error => {
            alert('Error happened while creating new simulation model.' + error);
        });
    }
    public deleteTradeSimulation(tradeSimulation: TradeSimulation) {
        this.dataService.getTradeInformation(tradeSimulation.modelId).subscribe(result => {
            if (result.tradeInstruments && result.tradeInstruments.length > 0) {
                this.processInstrumentsIntoTradeObject(tradeSimulation, result.tradeInstruments);
                this.dataService.deleteTradeSimulation(tradeSimulation).subscribe(success => {
                    if (!success) {
                        alert('Error 1 happened while deleting  simulation model.');
                    } else {
                        this.onNoModelSelectionChange();
                        //this.store.value.urlModelToggle[this.router.url] = false;
                        //this.store.set('urlModelToggle', this.store.value.urlModelToggle);

                        this.refreshModelList.emit(true);
                        this.dataService.deleteTradeNotifications(tradeSimulation.modelId).subscribe(success => {
                            if (!success) {
                                alert('Cannot delete notifications for model ' + tradeSimulation.modelId);
                            }
                            this.notifPollingService.restartNotificationPolling();
                        },
                            error => {
                            alert('Error 4 happened while deleting notifs of simulation model.');
                        });
                    }
                }, error => {
                    alert('Error 2 happened while deleting  simulation model.');
                });
            }
        }, error => {
            alert('Error 3 happened while getting trade data.');
        });
    }

    public processInstrumentsIntoTradeObject(tradeSimulation: TradeSimulation, instruments: InstrumentItem[]) {
        tradeSimulation.instruments = instruments;
        tradeSimulation.rowKeys = _.map(instruments, (x: InstrumentItem) => x.rowKey);
    }

    public computeLegalEntityName(entityId: string): string {
        const found = _.find(this.user.legalEntities, (x: any) => x.entityId === entityId);
        if (found) {
            return found.dataName;
        }
        return null;
    }

    public reloadPageData() {
        this.appEvents.triggerPageReload();
    }

    public getUserName(): string {
        return this.store.value.user && this.store.value.user.uid
            ? this.store.value.user.uid : '';
    }

    canEdit() {
        const userName = this.getUserName();
        if (userName === '') {
            return false;
        }
        if (!this.selectedModel) {
            return false;
        }
        const createdDay = moment.unix(Number(this.selectedModel.createdDate) / 1000).format('YDDMM');
        const now = moment().format('YDDMM');
        if (((Number(now) === Number(createdDay)) &&
            ((this.selectedModel.status === this.modelStatus.pending) ||
             (this.selectedModel.status === this.modelStatus.inProgress)))) {
            return false;
        }
        if (userName !== this.selectedModel.userName) {
            return false;
        }
        return true;
    }

    onModelSelectionChange(selectedItem) {
        this.selectedModel = selectedItem;
        this.modellingConfig.modelId = selectedItem.modelId;
    }

    onNoModelSelectionChange() {
        this.selectedModel = null;
        this.modellingConfig.modelId = null;
    }

    dumpInstruments() {
        const str = [];
        str.push(`Instrument, column Index, title, field`);
        for (const instrument of this.constantsService.instrumentConfigs) {
            const cols = [];
            for (const k in instrument.columns) {
                if (instrument.columns[k].enable) {
                    cols.push({ ...instrument.columns[k], name: k });
                }
            }
            cols.sort((a, b) => a.columnIndex - b.columnIndex);
            for (const c of cols) {
                str.push(`${instrument.instrument}, ${c.columnIndex}, ${c.columnName}, ${c.name}`);
            }
        }
        return str.join('\n');
    }
}